import {Record} from 'immutable';

import {makeReducer, match} from 'web-app/util/redux';

import * as FiltersAndColumnsActions from '../filters-and-columns/actions';
import * as Actions from './actions';
import * as Models from './models';
import {type SortOrder} from './constants';

type ResultsData = ReturnType<typeof Models.Results['default']['toObject']>;

export interface ResultsStateProps extends ResultsData {
    isFetching: boolean;
    sortBy: string;
    sortOrder: SortOrder;
    offset: number;
    hasFetchedResults: boolean;
}

export class ResultsState extends Record<ResultsStateProps>({
    ...Models.Results.default.toObject(),
    isFetching: false,
    sortBy: '',
    sortOrder: 'ASC',
    offset: 0,
    hasFetchedResults: false,
}) {}

const getSortOrder = (prevSortBy: string, nextSortBy: string, prevSortOrder: SortOrder, overwrite?: SortOrder) => {
    if (overwrite) {
        return overwrite;
    }

    if (prevSortBy === nextSortBy) {
        return prevSortOrder === 'ASC' ? 'DESC' : 'ASC';
    }
    return 'ASC';
};

export default makeReducer<ResultsState>(
    [
        match(Actions.fetchResults, (state, {payload}) =>
            state.merge({
                isFetching: true,
                offset: payload?.fetchMore ? state.offset : 0,
            }),
        ),
        match(Actions.setOffset, (state, {payload}) =>
            state.merge({
                offset: payload.offset,
            }),
        ),
        match(Actions.fetchResultsSuccess, (state, {payload}) => {
            return state.merge(payload.results, {
                isFetching: false,
                hasFetchedResults: true,
                offset: state.offset + payload.results.children.size + payload.results.employees.size,
                children: payload.fetchMore
                    ? state.children.concat(payload.results.children)
                    : payload.results.children,
                employees: payload.fetchMore
                    ? state.employees.concat(payload.results.employees)
                    : payload.results.employees,
            });
        }),
        match(Actions.fetchResultsFailed, state =>
            state.merge({
                isFetching: false,
            }),
        ),
        match(Actions.changeSortBy, (state, {payload}) =>
            state.merge({
                ...Models.Results.default.toObject(),
                isFetching: true,
                offset: 0,
                sortBy: payload.sortBy,
                sortOrder: getSortOrder(state.sortBy, payload.sortBy, state.sortOrder),
            }),
        ),
        match(FiltersAndColumnsActions.changeFiltersAndColumnsKind, () => new ResultsState()),
        match(Actions.setSortBy, (state, {payload}) =>
            state.merge({
                sortBy: payload.sortBy,
                sortOrder: getSortOrder(state.sortBy, payload.sortBy, state.sortOrder, payload.sortOrderOverwrite),
            }),
        ),
        match(Actions.reset, () => new ResultsState()),
    ],
    new ResultsState(),
);
